import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import { CollapsibleSection } from '@/components/atoms/CollapsibleSection/CollapsibleSection';
import { RelatedConversationsTile } from '@/components/atoms/RelatedConversations/RelatedConversationsTile';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useModalTryIt } from '@/hooks/useModalTryIt';
import { useRelatedConversations } from '@/modules/humanChat/hooks/useRelatedConversations';
import { selectSessionId } from '@/redux/session/selectors';

const MAX_PREVIEWS = 2;

export const RelatedConversationsSection = memo(() => {
  const [showAll, setShowAll] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const { relatedConversations, loading } = useRelatedConversations(undefined, isAccordionOpen);
  const { t } = useTranslation();
  const sessionId = useSelector(selectSessionId);
  const [searchParams] = useSearchParams();
  const searchSessionId = searchParams.get('session_id');
  const { handleAccountReplayClick } = useModalTryIt();
  const handledSessionRef = useRef<string | null>(null);

  useEffect(() => {
    if (!searchSessionId) {
      return;
    }
    if (handledSessionRef.current === searchSessionId) {
      return;
    }
    handledSessionRef.current = searchSessionId;
    handleAccountReplayClick(searchSessionId);
  }, [searchSessionId, handleAccountReplayClick]);

  const ref = useRef(null);

  // Collapse list and accordion when changing chat view
  useEffect(() => {
    if (sessionId !== ref.current) {
      setShowAll(false);
      setIsAccordionOpen(false);
      ref.current = sessionId;
    }
  }, [sessionId]);

  const handleClick = () => {
    setShowAll(true);
  };

  const handleOpenSession = useCallback(
    (session: string) => {
      handledSessionRef.current = session;
      handleAccountReplayClick(session);
    },
    [handleAccountReplayClick]
  );

  const handleAccordionToggle = useCallback((isOpen: boolean) => {
    setIsAccordionOpen(isOpen);
  }, []);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-1">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      );
    }
    if (!relatedConversations?.length) {
      return <div className="text-sm">{t('common.no_past_conversations')}</div>;
    }
    return (
      <ul className="flex h-full flex-col overflow-y-auto">
        {relatedConversations.slice(0, showAll ? undefined : MAX_PREVIEWS).map((chat) => (
          <RelatedConversationsTile
            key={chat.session_id}
            session_id={chat.session_id}
            onOpen={() => handleOpenSession(chat.session_id)}
          />
        ))}
        {!showAll && relatedConversations?.length > 2 && (
          <Button
            onClick={handleClick}
            variant="link-secondary"
            size="sm"
            className="h-auto justify-start px-0 py-0.5 text-sm text-foreground underline"
          >
            {t('common.show_all')}
          </Button>
        )}
      </ul>
    );
  };

  return (
    <section className="h-auto overflow-hidden">
      <CollapsibleSection
        title={t('common.past_conversations')}
        open={false}
        showBorder={false}
        onToggle={handleAccordionToggle}
      >
        <div className="px-1.5 pb-3">{renderContent()}</div>
      </CollapsibleSection>
    </section>
  );
});

RelatedConversationsSection.displayName = 'RelatedConversationsSection';
